// SVG chart renderers for the chart-maker tool family. Pure functions from data
// to an SVG string, no chart library: the marks these pages need (pie, bar,
// line, venn, timeline) are a few hundred lines of geometry, and a dependency
// would cost more bundle than the whole engine.
//
// Color rules follow the dataviz method: categorical hues in FIXED order (pie
// slices and venn sets are identities), a SINGLE hue for one-series bars and
// lines (magnitude, not identity), values labeled directly because two palette
// steps sit below 3:1 contrast against the surface. The 6-slot palette passes
// the CVD validator (worst adjacent pair ΔE 9.1 protan) on both surfaces.

export const CHART_SERIES_COLORS = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#4a3aa7',
] as const;

const SURFACE = '#ffffff';
const INK = '#1f2933';
const INK_MUTED = '#5f6b76';
const GRID = '#e3e7eb';
const FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

export type ChartRow = { label: string; value: number };

export type ParsedChartData = { rows: ChartRow[]; errors: string[] };

// Accepts "Label, 12" / "Label\t12" / "Label 12" one per line. Forgiving on
// purpose: this is paste-from-a-spreadsheet territory.
export function parseChartRows(text: string, maxRows = 30): ParsedChartData {
  const rows: ChartRow[] = [];
  const errors: string[] = [];

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;

    const match =
      line.match(/^(.*?)[,;\t]\s*(-?[\d\s.,]+)$/) || line.match(/^(.*\S)\s+(-?[\d.,]+)$/);
    if (!match) {
      errors.push(`Could not read "${line}". Use one "Label, value" per line.`);
      continue;
    }

    const label = match[1].trim().replace(/[,;]$/, '');
    const value = Number(match[2].replace(/\s/g, '').replace(',', '.'));

    if (!label || !Number.isFinite(value)) {
      errors.push(`Could not read "${line}". Use one "Label, value" per line.`);
      continue;
    }

    rows.push({ label, value });
    if (rows.length >= maxRows) break;
  }

  return { rows, errors };
}

function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatValue(value: number): string {
  if (Math.abs(value) >= 1000) return value.toLocaleString('en-US');
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function svgShell(width: number, height: number, title: string, body: string): string {
  const titleBlock = title
    ? `<text x="${width / 2}" y="26" text-anchor="middle" font-family="${FONT}" font-size="17" font-weight="600" fill="${INK}">${esc(title)}</text>`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img"><rect width="${width}" height="${height}" fill="${SURFACE}"/>${titleBlock}${body}</svg>`;
}

function legend(rows: ChartRow[], x: number, y: number, lineHeight = 22): string {
  return rows
    .map(
      (row, i) =>
        `<g><rect x="${x}" y="${y + i * lineHeight}" width="12" height="12" rx="3" fill="${CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length]}"/><text x="${x + 18}" y="${y + i * lineHeight + 10}" font-family="${FONT}" font-size="13" fill="${INK}">${esc(row.label)}</text></g>`,
    )
    .join('');
}

// Slices beyond the palette fold into "Other" rather than cycling hues: a
// repeated hue lies about identity, an "Other" slice only admits aggregation.
export function foldRowsForPie(rows: ChartRow[]): ChartRow[] {
  const positive = rows.filter((row) => row.value > 0);
  if (positive.length <= CHART_SERIES_COLORS.length) return positive;

  const sorted = [...positive].sort((a, b) => b.value - a.value);
  const kept = sorted.slice(0, CHART_SERIES_COLORS.length - 1);
  const rest = sorted.slice(CHART_SERIES_COLORS.length - 1);
  return [...kept, { label: 'Other', value: rest.reduce((sum, row) => sum + row.value, 0) }];
}

export function renderPieChartSvg(
  inputRows: ChartRow[],
  options: { title?: string; donut?: boolean } = {},
): string {
  const rows = foldRowsForPie(inputRows);
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  if (!rows.length || total <= 0) return '';

  const width = 640;
  const height = 400;
  const cx = 220;
  const cy = 210;
  const radius = 150;
  const inner = options.donut ? 78 : 0;

  let angle = -Math.PI / 2;
  const slices = rows
    .map((row, i) => {
      const sweep = (row.value / total) * Math.PI * 2;
      const a0 = angle;
      const a1 = angle + sweep;
      angle = a1;

      const large = sweep > Math.PI ? 1 : 0;
      const x0 = cx + radius * Math.cos(a0);
      const y0 = cy + radius * Math.sin(a0);
      const x1 = cx + radius * Math.cos(a1);
      const y1 = cy + radius * Math.sin(a1);

      let path: string;
      if (inner > 0) {
        const ix0 = cx + inner * Math.cos(a1);
        const iy0 = cy + inner * Math.sin(a1);
        const ix1 = cx + inner * Math.cos(a0);
        const iy1 = cy + inner * Math.sin(a0);
        path = `M ${x0} ${y0} A ${radius} ${radius} 0 ${large} 1 ${x1} ${y1} L ${ix0} ${iy0} A ${inner} ${inner} 0 ${large} 0 ${ix1} ${iy1} Z`;
      } else {
        path = `M ${cx} ${cy} L ${x0} ${y0} A ${radius} ${radius} 0 ${large} 1 ${x1} ${y1} Z`;
      }

      // The 2px surface-colored stroke is the mandatory gap between fills.
      const slice = `<path d="${path}" fill="${CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length]}" stroke="${SURFACE}" stroke-width="2"/>`;

      const share = row.value / total;
      if (share < 0.05) return slice;
      const mid = (a0 + a1) / 2;
      const labelRadius = inner > 0 ? (radius + inner) / 2 : radius * 0.62;
      const lx = cx + labelRadius * Math.cos(mid);
      const ly = cy + labelRadius * Math.sin(mid);
      return `${slice}<text x="${lx}" y="${ly}" text-anchor="middle" dominant-baseline="middle" font-family="${FONT}" font-size="13" font-weight="600" fill="${SURFACE}">${Math.round(share * 100)}%</text>`;
    })
    .join('');

  return svgShell(width, height, options.title ?? '', slices + legend(rows, 420, 90));
}

export function renderBarChartSvg(
  rows: ChartRow[],
  options: { title?: string; horizontal?: boolean } = {},
): string {
  if (!rows.length) return '';

  const width = 640;
  const height = 400;
  const pad = { top: 48, right: 24, bottom: 64, left: 56 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const max = Math.max(...rows.map((row) => row.value), 0);
  if (max <= 0) return '';

  // One series, one hue: bars measure magnitude, they are not six identities.
  const color = CHART_SERIES_COLORS[0];
  const ticks = 4;
  const grid = Array.from({ length: ticks + 1 }, (_, i) => {
    const value = (max / ticks) * i;
    if (options.horizontal) {
      const x = pad.left + (plotW * i) / ticks;
      return `<line x1="${x}" y1="${pad.top}" x2="${x}" y2="${pad.top + plotH}" stroke="${GRID}" stroke-width="1"/><text x="${x}" y="${pad.top + plotH + 18}" text-anchor="middle" font-family="${FONT}" font-size="11" fill="${INK_MUTED}">${formatValue(value)}</text>`;
    }
    const y = pad.top + plotH - (plotH * i) / ticks;
    return `<line x1="${pad.left}" y1="${y}" x2="${pad.left + plotW}" y2="${y}" stroke="${GRID}" stroke-width="1"/><text x="${pad.left - 8}" y="${y + 4}" text-anchor="end" font-family="${FONT}" font-size="11" fill="${INK_MUTED}">${formatValue(value)}</text>`;
  }).join('');

  const band = (options.horizontal ? plotH : plotW) / rows.length;
  const barSize = Math.min(band * 0.68, 56);

  const bars = rows
    .map((row, i) => {
      const size = (row.value / max) * (options.horizontal ? plotW : plotH);
      if (options.horizontal) {
        const y = pad.top + band * i + (band - barSize) / 2;
        return `<rect x="${pad.left}" y="${y}" width="${Math.max(size, 1)}" height="${barSize}" rx="4" fill="${color}"/><text x="${pad.left + size + 6}" y="${y + barSize / 2 + 4}" font-family="${FONT}" font-size="12" fill="${INK}">${formatValue(row.value)}</text><text x="${pad.left - 8}" y="${y + barSize / 2 + 4}" text-anchor="end" font-family="${FONT}" font-size="12" fill="${INK}">${esc(row.label)}</text>`;
      }
      const x = pad.left + band * i + (band - barSize) / 2;
      const y = pad.top + plotH - size;
      return `<rect x="${x}" y="${y}" width="${barSize}" height="${Math.max(size, 1)}" rx="4" fill="${color}"/><text x="${x + barSize / 2}" y="${y - 6}" text-anchor="middle" font-family="${FONT}" font-size="12" fill="${INK}">${formatValue(row.value)}</text><text x="${x + barSize / 2}" y="${pad.top + plotH + 18}" text-anchor="middle" font-family="${FONT}" font-size="11" fill="${INK_MUTED}">${esc(row.label.length > 12 ? `${row.label.slice(0, 11)}…` : row.label)}</text>`;
    })
    .join('');

  const axisLabels = options.horizontal
    ? ''
    : `<line x1="${pad.left}" y1="${pad.top + plotH}" x2="${pad.left + plotW}" y2="${pad.top + plotH}" stroke="${INK_MUTED}" stroke-width="1"/>`;

  return svgShell(width, height, options.title ?? '', grid + axisLabels + bars);
}

export function renderLineChartSvg(rows: ChartRow[], options: { title?: string } = {}): string {
  if (rows.length < 2) return '';

  const width = 640;
  const height = 400;
  const pad = { top: 48, right: 60, bottom: 56, left: 56 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const values = rows.map((row) => row.value);
  const max = Math.max(...values);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const color = CHART_SERIES_COLORS[0];

  const point = (row: ChartRow, i: number) => ({
    x: pad.left + (plotW * i) / (rows.length - 1),
    y: pad.top + plotH - ((row.value - min) / span) * plotH,
  });

  const ticks = 4;
  const grid = Array.from({ length: ticks + 1 }, (_, i) => {
    const value = min + (span / ticks) * i;
    const y = pad.top + plotH - (plotH * i) / ticks;
    return `<line x1="${pad.left}" y1="${y}" x2="${pad.left + plotW}" y2="${y}" stroke="${GRID}" stroke-width="1"/><text x="${pad.left - 8}" y="${y + 4}" text-anchor="end" font-family="${FONT}" font-size="11" fill="${INK_MUTED}">${formatValue(value)}</text>`;
  }).join('');

  const path = rows
    .map((row, i) => {
      const p = point(row, i);
      return `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
    })
    .join(' ');

  const markers = rows
    .map((row, i) => {
      const p = point(row, i);
      const xLabel =
        rows.length <= 12 || i % Math.ceil(rows.length / 12) === 0
          ? `<text x="${p.x}" y="${pad.top + plotH + 18}" text-anchor="middle" font-family="${FONT}" font-size="11" fill="${INK_MUTED}">${esc(row.label.length > 10 ? `${row.label.slice(0, 9)}…` : row.label)}</text>`
          : '';
      return `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="4" fill="${color}" stroke="${SURFACE}" stroke-width="2"/>${xLabel}`;
    })
    .join('');

  // Direct label on the last point instead of a legend: one series needs no box.
  const last = point(rows[rows.length - 1], rows.length - 1);
  const endLabel = `<text x="${last.x + 8}" y="${last.y + 4}" font-family="${FONT}" font-size="12" font-weight="600" fill="${INK}">${formatValue(rows[rows.length - 1].value)}</text>`;

  return svgShell(
    width,
    height,
    options.title ?? '',
    `${grid}<path d="${path}" fill="none" stroke="${color}" stroke-width="2"/>${markers}${endLabel}`,
  );
}

export type VennInput = {
  aLabel: string;
  aSize: number;
  bLabel: string;
  bSize: number;
  overlap: number;
};

// Two circles, radius by sqrt(size) so AREA tracks the number, overlap distance
// solved so the lens is proportional-ish. This is a diagram, not exact-area math,
// and the counts are printed so the numbers never depend on the geometry.
export function renderVennSvg(input: VennInput, options: { title?: string } = {}): string {
  const { aSize, bSize } = input;
  const overlap = Math.min(input.overlap, aSize, bSize);
  if (aSize <= 0 || bSize <= 0) return '';

  const width = 640;
  const height = 400;
  const cy = 210;
  const maxR = 118;
  const scale = maxR / Math.sqrt(Math.max(aSize, bSize));
  const ra = Math.max(Math.sqrt(aSize) * scale, 40);
  const rb = Math.max(Math.sqrt(bSize) * scale, 40);
  const overlapShare = overlap / Math.min(aSize, bSize);
  const distance = (ra + rb) * (1 - 0.55 * overlapShare);
  const ax = width / 2 - distance / 2;
  const bx = width / 2 + distance / 2;

  const body = `
    <circle cx="${ax}" cy="${cy}" r="${ra}" fill="${CHART_SERIES_COLORS[0]}" fill-opacity="0.55" stroke="${CHART_SERIES_COLORS[0]}" stroke-width="2"/>
    <circle cx="${bx}" cy="${cy}" r="${rb}" fill="${CHART_SERIES_COLORS[1]}" fill-opacity="0.55" stroke="${CHART_SERIES_COLORS[1]}" stroke-width="2"/>
    <text x="${ax - ra * 0.35}" y="${cy - 6}" text-anchor="middle" font-family="${FONT}" font-size="14" font-weight="600" fill="${INK}">${esc(input.aLabel)}</text>
    <text x="${ax - ra * 0.35}" y="${cy + 14}" text-anchor="middle" font-family="${FONT}" font-size="13" fill="${INK}">${formatValue(aSize - overlap)}</text>
    <text x="${bx + rb * 0.35}" y="${cy - 6}" text-anchor="middle" font-family="${FONT}" font-size="14" font-weight="600" fill="${INK}">${esc(input.bLabel)}</text>
    <text x="${bx + rb * 0.35}" y="${cy + 14}" text-anchor="middle" font-family="${FONT}" font-size="13" fill="${INK}">${formatValue(bSize - overlap)}</text>
    ${
      overlap > 0
        ? `<text x="${width / 2}" y="${cy + 4}" text-anchor="middle" font-family="${FONT}" font-size="13" font-weight="600" fill="${INK}">${formatValue(overlap)}</text>`
        : ''
    }`;

  return svgShell(width, height, options.title ?? '', body);
}

// Alternating above/below the spine so long titles have room to breathe.
export function renderTimelineSvg(
  rows: ChartRow[] | { label: string; title: string }[],
  options: { title?: string } = {},
): string {
  const events = rows
    .map((row) => ('title' in row ? row : { label: row.label, title: formatValue(row.value) }))
    .filter((event) => event.label);
  if (events.length < 2) return '';

  const width = 720;
  const height = 320;
  const pad = 70;
  const spineY = height / 2 + 10;
  const step = (width - pad * 2) / (events.length - 1);

  const marks = events
    .map((event, i) => {
      const x = pad + step * i;
      const above = i % 2 === 0;
      const textY = above ? spineY - 46 : spineY + 40;
      const dateY = above ? spineY - 28 : spineY + 58;
      return `
      <line x1="${x}" y1="${spineY}" x2="${x}" y2="${above ? spineY - 20 : spineY + 20}" stroke="${INK_MUTED}" stroke-width="1"/>
      <circle cx="${x}" cy="${spineY}" r="6" fill="${CHART_SERIES_COLORS[0]}" stroke="${SURFACE}" stroke-width="2"/>
      <text x="${x}" y="${textY}" text-anchor="middle" font-family="${FONT}" font-size="13" font-weight="600" fill="${INK}">${esc(event.title.length > 24 ? `${event.title.slice(0, 23)}…` : event.title)}</text>
      <text x="${x}" y="${dateY}" text-anchor="middle" font-family="${FONT}" font-size="12" fill="${INK_MUTED}">${esc(event.label)}</text>`;
    })
    .join('');

  return svgShell(
    width,
    height,
    options.title ?? '',
    `<line x1="${pad - 20}" y1="${spineY}" x2="${width - pad + 20}" y2="${spineY}" stroke="${INK_MUTED}" stroke-width="2"/>${marks}`,
  );
}
